import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAllAds, useCreateAd, useUpdateAd, useDeleteAd } from "@/hooks/useAds";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  Edit, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  XCircle,
  TrendingUp,
  MousePointerClick,
  DollarSign,
  BarChart3,
  Image as ImageIcon,
  ExternalLink,
  ArrowLeft,
  ArrowRight,
  Upload,
  Trash2 as Trash2Icon,
  Megaphone
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface AdFormData {
  advertiser_name: string;
  advertiser_email: string;
  title: string;
  title_ar: string;
  description: string;
  description_ar: string;
  image_url: string;
  link_url: string;
  ad_type: 'banner' | 'sidebar';
  pricing_model: 'cpm' | 'cpc';
  price_per_unit: number;
  budget: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_approved: boolean;
  target_categories: string[];
}

const initialFormData: AdFormData = {
  advertiser_name: '',
  advertiser_email: '',
  title: '',
  title_ar: '',
  description: '',
  description_ar: '',
  image_url: '',
  link_url: '',
  ad_type: 'banner',
  pricing_model: 'cpm',
  price_per_unit: 5,
  budget: 100,
  start_date: '',
  end_date: '',
  is_active: false,
  is_approved: false,
  target_categories: [],
};

export default function AdsManagement() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const { data: ads, isLoading } = useAllAds();
  const createAd = useCreateAd();
  const updateAd = useUpdateAd();
  const deleteAd = useDeleteAd();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState<AdFormData>(initialFormData);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);


  // Stats calculation
  const totalImpressions = ads?.reduce((sum, ad) => sum + (ad.impressions || 0), 0) || 0;
  const totalClicks = ads?.reduce((sum, ad) => sum + (ad.clicks || 0), 0) || 0;
  const totalSpent = ads?.reduce((sum, ad) => sum + Number(ad.spent || 0), 0) || 0;
  const averageCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(isRTL ? 'حجم الملف أكبر من 5 ميجابايت' : 'File size exceeds 5MB');
      return;
    }

    setUploading(true);
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `ads/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('ad-images')
      .upload(filePath, file);

    if (uploadError) {
      toast.error(isRTL ? 'فشل رفع الصورة' : 'Failed to upload image');
      setUploading(false);
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('ad-images')
      .getPublicUrl(filePath);

    setFormData(prev => ({ ...prev, image_url: publicUrl }));
    setUploading(false);
    toast.success(isRTL ? 'تم رفع الصورة' : 'Image uploaded');
  };

  const handleSubmit = async () => {
    if (!formData.advertiser_name || !formData.title || !formData.image_url || !formData.link_url) {
      toast.error(isRTL ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    try {
      if (editingId) {
        await updateAd.mutateAsync({ id: editingId, ...formData });
        toast.success(isRTL ? 'تم تحديث الإعلان' : 'Ad updated');
      } else {
        await createAd.mutateAsync(formData);
        toast.success(isRTL ? 'تم إنشاء الإعلان' : 'Ad created');
      }
      setFormData(initialFormData);
      setEditingId(null);
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(isRTL ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleEdit = (ad: any) => {
    setFormData({
      advertiser_name: ad.advertiser_name,
      advertiser_email: ad.advertiser_email || '',
      title: ad.title,
      title_ar: ad.title_ar || '',
      description: ad.description || '',
      description_ar: ad.description_ar || '',
      image_url: ad.image_url,
      link_url: ad.link_url,
      ad_type: ad.ad_type,
      pricing_model: ad.pricing_model,
      price_per_unit: ad.price_per_unit,
      budget: ad.budget,
      start_date: ad.start_date ? ad.start_date.split('T')[0] : '',
      end_date: ad.end_date ? ad.end_date.split('T')[0] : '',
      is_active: ad.is_active,
      is_approved: ad.is_approved,
      target_categories: ad.target_categories || [],
    });
    setEditingId(ad.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm(isRTL ? 'هل أنت متأكد من حذف هذا الإعلان؟' : 'Are you sure you want to delete this ad?')) {
      await deleteAd.mutateAsync(id);
      toast.success(isRTL ? 'تم حذف الإعلان' : 'Ad deleted');
    }
  };

  const toggleActive = async (id: string, currentState: boolean) => {
    await updateAd.mutateAsync({ id, is_active: !currentState });
  };

  const toggleApproved = async (id: string, currentState: boolean) => {
    await updateAd.mutateAsync({ id, is_approved: !currentState });
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate("/admin-dashboard")} 
        className="flex items-center gap-2 mb-4"
      >
        {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
        {isRTL ? 'العودة للوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">{isRTL ? 'إدارة الإعلانات' : 'Ads Management'}</h1>
          <p className="text-muted-foreground mt-1">
            {isRTL ? 'إدارة الإعلانات المدفوعة وتتبع الأداء' : 'Manage paid ads and track performance'}
          </p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setFormData(initialFormData); setEditingId(null); }}>
              <Plus className="w-4 h-4 mr-2" />
              {isRTL ? 'إضافة إعلان' : 'Add Ad'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingId ? (isRTL ? 'تعديل الإعلان' : 'Edit Ad') : (isRTL ? 'إضافة إعلان جديد' : 'Add New Ad')}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'اسم المعلن *' : 'Advertiser Name *'}</Label>
                  <Input 
                    value={formData.advertiser_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, advertiser_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'البريد الإلكتروني' : 'Email'}</Label>
                  <Input 
                    type="email"
                    value={formData.advertiser_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, advertiser_email: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'العنوان (إنجليزي) *' : 'Title (English) *'}</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'العنوان (عربي)' : 'Title (Arabic)'}</Label>
                  <Input 
                    value={formData.title_ar}
                    onChange={(e) => setFormData(prev => ({ ...prev, title_ar: e.target.value }))}
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'الوصف (إنجليزي)' : 'Description (English)'}</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الوصف (عربي)' : 'Description (Arabic)'}</Label>
                  <Textarea 
                    value={formData.description_ar}
                    onChange={(e) => setFormData(prev => ({ ...prev, description_ar: e.target.value }))}
                    rows={2}
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{isRTL ? 'صورة الإعلان *' : 'Ad Image *'}</Label>
                <p className="text-xs text-muted-foreground">
                  {formData.ad_type === 'banner'
                    ? (isRTL ? 'المقاس الموصى به: 1200×200 بكسل (بانر علوي)' : 'Recommended size: 1200×200 px (Top Banner)')
                    : (isRTL ? 'المقاس الموصى به: 300×250 بكسل (إعلان جانبي)' : 'Recommended size: 300×250 px (Sidebar)')
                  }
                </p>
                <div className="flex gap-4 items-center">
                  <Input 
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                  />
                  {formData.image_url && (
                    <img src={formData.image_url} alt="Preview" className="w-16 h-16 object-cover rounded" />
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>{isRTL ? 'رابط الإعلان *' : 'Ad Link *'}</Label>
                <Input 
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'نوع الإعلان' : 'Ad Type'}</Label>
                  <Select 
                    value={formData.ad_type} 
                    onValueChange={(v: 'banner' | 'sidebar') => setFormData(prev => ({ ...prev, ad_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="banner">{isRTL ? 'بانر علوي' : 'Top Banner'}</SelectItem>
                      <SelectItem value="sidebar">{isRTL ? 'إعلان جانبي' : 'Sidebar'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'نموذج التسعير' : 'Pricing Model'}</Label>
                  <Select 
                    value={formData.pricing_model} 
                    onValueChange={(v: 'cpm' | 'cpc') => setFormData(prev => ({ ...prev, pricing_model: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cpm">CPM ({isRTL ? 'لكل 1000 مشاهدة' : 'per 1000 views'})</SelectItem>
                      <SelectItem value="cpc">CPC ({isRTL ? 'لكل نقرة' : 'per click'})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'السعر (ر.س)' : 'Price (SAR)'}</Label>
                  <Input 
                    type="number"
                    value={formData.price_per_unit}
                    onChange={(e) => setFormData(prev => ({ ...prev, price_per_unit: Number(e.target.value) }))}
                    min={0}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'الميزانية (ر.س)' : 'Budget (SAR)'}</Label>
                  <Input 
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData(prev => ({ ...prev, budget: Number(e.target.value) }))}
                    min={0}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ البدء' : 'Start Date'}</Label>
                  <Input 
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? 'تاريخ الانتهاء' : 'End Date'}</Label>
                  <Input 
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))}
                  />
                  <Label>{isRTL ? 'نشط' : 'Active'}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={formData.is_approved}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_approved: v }))}
                  />
                  <Label>{isRTL ? 'معتمد' : 'Approved'}</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">{isRTL ? 'إلغاء' : 'Cancel'}</Button>
              </DialogClose>
              <Button onClick={handleSubmit} disabled={createAd.isPending || updateAd.isPending}>
                {editingId ? (isRTL ? 'تحديث' : 'Update') : (isRTL ? 'إنشاء' : 'Create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-500/10">
              <TrendingUp className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{isRTL ? 'المشاهدات' : 'Impressions'}</p>
              <p className="text-2xl font-bold">{totalImpressions.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-500/10">
              <MousePointerClick className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{isRTL ? 'النقرات' : 'Clicks'}</p>
              <p className="text-2xl font-bold">{totalClicks.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-purple-500/10">
              <BarChart3 className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{isRTL ? 'معدل النقر' : 'CTR'}</p>
              <p className="text-2xl font-bold">{averageCTR}%</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-amber-500/10">
              <DollarSign className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{isRTL ? 'الإنفاق' : 'Spent'}</p>
              <p className="text-2xl font-bold">{totalSpent.toFixed(2)} {isRTL ? 'ر.س' : 'SAR'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ads Table */}
      <Card>
        <CardHeader>
          <CardTitle>{isRTL ? 'جميع الإعلانات' : 'All Ads'}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all">
            <TabsList className="mb-4">
              <TabsTrigger value="all">{isRTL ? 'الكل' : 'All'}</TabsTrigger>
              <TabsTrigger value="banner">{isRTL ? 'بانر' : 'Banner'}</TabsTrigger>
              <TabsTrigger value="sidebar">{isRTL ? 'جانبي' : 'Sidebar'}</TabsTrigger>
            </TabsList>

            {['all', 'banner', 'sidebar'].map((tab) => (
              <TabsContent key={tab} value={tab}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{isRTL ? 'الصورة' : 'Image'}</TableHead>
                      <TableHead>{isRTL ? 'المعلن' : 'Advertiser'}</TableHead>
                      <TableHead>{isRTL ? 'العنوان' : 'Title'}</TableHead>
                      <TableHead>{isRTL ? 'النوع' : 'Type'}</TableHead>
                      <TableHead>{isRTL ? 'التسعير' : 'Pricing'}</TableHead>
                      <TableHead>{isRTL ? 'المشاهدات' : 'Views'}</TableHead>
                      <TableHead>{isRTL ? 'النقرات' : 'Clicks'}</TableHead>
                      <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                      <TableHead>{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">
                          {isRTL ? 'جاري التحميل...' : 'Loading...'}
                        </TableCell>
                      </TableRow>
                    ) : ads?.filter(ad => tab === 'all' || ad.ad_type === tab).map((ad) => (
                      <TableRow key={ad.id}>
                        <TableCell>
                          <img src={ad.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                        </TableCell>
                        <TableCell>{ad.advertiser_name}</TableCell>
                        <TableCell>{isRTL && ad.title_ar ? ad.title_ar : ad.title}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {ad.ad_type === 'banner' ? (isRTL ? 'بانر' : 'Banner') : (isRTL ? 'جانبي' : 'Sidebar')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {ad.pricing_model.toUpperCase()} - {ad.price_per_unit} {isRTL ? 'ر.س' : 'SAR'}
                          </Badge>
                        </TableCell>
                        <TableCell>{ad.impressions?.toLocaleString() || 0}</TableCell>
                        <TableCell>{ad.clicks?.toLocaleString() || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Badge 
                              variant={ad.is_active ? "default" : "secondary"}
                              className="cursor-pointer"
                              onClick={() => toggleActive(ad.id, ad.is_active)}
                            >
                              {ad.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            </Badge>
                            <Badge 
                              variant={ad.is_approved ? "default" : "destructive"}
                              className="cursor-pointer"
                              onClick={() => toggleApproved(ad.id, ad.is_approved)}
                            >
                              {ad.is_approved ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(ad)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" asChild>
                              <a href={ad.link_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(ad.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
